import {
  useState, useEffect, useRef, useCallback,
} from 'react';

import qapi from 'views_shared/app/javascript/api';

/**
 * Normalizes a search API result into the conversation object shape
 * expected by the Conversation component. Conversationless-pitch results
 * (type "pitch") normalize into the merged feed's virtual-row shape instead,
 * so ConversationsList renders them as PitchRow.
 */
function normalizeSearchResult(result) {
  const attrs = result.attributes;
  if (result.type === 'pitch') {
    return {
      feed_row_type: 'pitch',
      conversation_id: result.id, // "pitch-<id>", stable list key
      pitch: {
        id: attrs.pitch_id,
        migrate_id: attrs.migrate_id,
        sent_at: attrs.sent_at,
        created_at: attrs.created_at,
        read_at: attrs.read_at,
        pitch_text: attrs.pitch_text,
        content_format: attrs.content_format,
        profile_name: attrs.profile_name,
        profile_slug: attrs.profile_slug,
        profile_image_url: attrs.profile_image_url,
        profile_title_at_workplace: attrs.profile_title_at_workplace,
        profile_workplace: attrs.profile_workplace,
        type_of_profile: attrs.type_of_profile,
        request_title: attrs.request_title,
        request_slug: attrs.request_slug,
        pitch_attachments: attrs.pitch_attachments,
        user_under_audit_at: attrs.user_under_audit_at,
        user_migrate_id: attrs.user_migrate_id,
        search_hits: attrs.hits,
      },
    };
  }
  return {
    id: result.id,
    conversation_id: result.id,
    conversation_senders: JSON.parse(attrs.conversation_senders),
    conversation_receivers: JSON.parse(attrs.conversation_receivers),
    last_sender_slug: attrs.last_sender_slug,
    last_sent_at: attrs.last_sent_at,
    last_body: attrs.last_body,
    subject: attrs.subject,
    origin_id: attrs.origin_id,
    origin_type: attrs.origin_type,
    receipt_read_at: attrs.receipt_read_at,
    receipt_archived_at: attrs.receipt_archived_at,
    conversation_mailbox: attrs.conversation_mailbox,
    search_hits: attrs.hits,
  };
}

export const SEARCH_PAGE_SIZE = 10;

// A short page is the last one, even if the page count says otherwise (an older API overcounted).
function hasMorePages(meta, rowCount) {
  if (rowCount < SEARCH_PAGE_SIZE) {
    return false;
  }
  const pagination = meta?.pagination;
  return pagination ? pagination.current_page < pagination.total_pages : true;
}

function appendUnique(existingRows, newRows) {
  const seen = new Set(existingRows.map((row) => row.conversation_id));
  return existingRows.concat(newRows.filter((row) => !seen.has(row.conversation_id)));
}

/**
 * Hook for searching conversations via the conversation_searches API, one page at a time.
 *
 * @param {string} searchQuery - The search text (null/empty = no search active)
 * @param {string} currentReceiverSlug - The current user's profile slug
 * @returns {{ searchResults: Array, isSearching: boolean, hasMore: boolean, isLoadingMore: boolean, loadMore: Function }}
 */
function useSearchConversations(searchQuery, currentReceiverSlug, filters = null) {
  // Filters re-run the search like a new query does; the key keeps the effect deps stable.
  const filtersKey = JSON.stringify(filters || {});
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const debounceTimer = useRef(null);
  const abortControllerRef = useRef(new AbortController());
  const activeSearchRef = useRef(null);
  const pageRef = useRef(1);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    activeSearchRef.current = null;
    loadingMoreRef.current = false;
    setHasMore(false);
    setIsLoadingMore(false);

    if (!searchQuery) {
      setSearchResults([]);
      setIsSearching(false);
      return () => {};
    }

    setIsSearching(true);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      const activeSearch = { query: searchQuery.trim(), filters };
      activeSearchRef.current = activeSearch;
      pageRef.current = 1;

      qapi.conversations
        .search(activeSearch.query, {
          signal: abortControllerRef.current.signal,
          filters,
          page: 1,
          pageSize: SEARCH_PAGE_SIZE,
        })
        .then(({ data, meta }) => {
          if (data) {
            setSearchResults(data.map(normalizeSearchResult));
            setHasMore(hasMorePages(meta, data.length));
          }
          setIsSearching(false);
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            throw error;
          }
        });
    }, 500);

    return () => {
      clearTimeout(debounceTimer.current);
      abortControllerRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filtersKey stands in for filters
  }, [searchQuery, currentReceiverSlug, filtersKey]);

  const loadMore = useCallback(() => {
    const activeSearch = activeSearchRef.current;
    if (!activeSearch || !hasMore || isSearching || loadingMoreRef.current) {
      return;
    }

    const nextPage = pageRef.current + 1;
    loadingMoreRef.current = true;
    setIsLoadingMore(true);

    qapi.conversations
      .search(activeSearch.query, {
        signal: abortControllerRef.current.signal,
        filters: activeSearch.filters,
        page: nextPage,
        pageSize: SEARCH_PAGE_SIZE,
      })
      .then(({ data, meta }) => {
        if (activeSearchRef.current !== activeSearch) {
          return;
        }
        if (data) {
          pageRef.current = nextPage;
          setSearchResults((rows) => appendUnique(rows, data.map(normalizeSearchResult)));
          setHasMore(hasMorePages(meta, data.length));
        } else {
          // A failed page stops paging; otherwise the list's observer would retry it in a loop.
          setHasMore(false);
        }
        loadingMoreRef.current = false;
        setIsLoadingMore(false);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          throw error;
        }
      });
  }, [hasMore, isSearching]);

  return {
    searchResults, isSearching, hasMore, isLoadingMore, loadMore,
  };
}

export default useSearchConversations;
